import { usePagination, usePathParam } from '@chilecompra/react-kit'
import { Card, Pagination, Skeleton, Table, TableRow, Tag, Toast, Typography } from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { useScore } from '../../components/Score/Score.hooks'
import { SCORE_RANGE_COLORS, VOID_STATES } from '../../config/settings/constant'
import { PUBLIC_URL } from '../../config/settings/environment'
import { PATH_PARAM } from '../../config/settings/regexes'
import routes from '../../config/settings/routes'
import { onSetInitialState } from '../Sanction/Sanction.actions'
import { Container, Link, NotFoundImg, Strong, TableCell, TableContainer } from './SanctionsTypeTable.styles'

const ROWS_PER_PAGE = 5

const SANCTION_TYPES = {
  agilePurchase: { label: 'Compra Ágil', titleLabel: 'Compra Ágil' },
  directDeals: { label: 'Trato Directo', titleLabel: 'Tratos Directos' },
  frameworkAgreement: { label: 'Convenio Marco', titleLabel: 'Convenio Marco' },
  tenders: { label: 'Licitaciones', titleLabel: 'Licitaciones' }
}

const TABLE_COLUMNS = [
  { key: 'purchaseOrder', label: 'Orden de compra', width: '200px' },
  { key: 'organization', label: 'Organismo', width: '220px' },
  { key: 'date', label: 'Aceptación OC', width: '120px' },
  { key: 'detail', label: 'Detalle', width: '80px' }
]

/**
 * SantionsTypeTable container.
 */
const SantionsTypeTable = ({ type }) => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')

  const { loading, loadingAllSanctions, max } = useSelector(state => state.contractualBehavior)
  const { rate, sanctions } = useSelector(state => state.contractualBehavior[type])

  const { updatedRate } = useScore({
    max,
    rangeValues: SCORE_RANGE_COLORS,
    rate
  })

  const { onChangePage, onGetCurrentPage, page, totalPages } = usePagination({
    data: sanctions,
    initialPage: 1,
    rowsPerPage: ROWS_PER_PAGE
  })

  const handleOnClick = () => dispatch(onSetInitialState())

  const { label, titleLabel } = SANCTION_TYPES[type]
  const title = `Detalle de sanciones recibidas en ${titleLabel}`
  const isLoading = loading || loadingAllSanctions

  return (
    <Container gap="24px" padding="32px 0 16px">
      <Container alignItems="baseline" gap="8px" justifyContent="space-between">
        <Typography fontWeight="bold" variant="h4">
          {title}
        </Typography>

        <Toast disableIcon padding="4px 16px" severity="default">
          <Container gap="4px">
            <Typography variant="subtitle">Nota comportamiento {label}:</Typography>

            <Typography variant="subtitle">
              {isLoading ? (
                <Skeleton height="100%" padding="0 10px" variant="rect" width="100px" />
              ) : (
                <Strong>{rate === -1 || updatedRate === '0,00' ? 'Sin información' : `${updatedRate}/${max}`}</Strong>
              )}
            </Typography>
          </Container>
        </Toast>
      </Container>

      {isLoading && <TablePlaceholder />}

      {!isLoading && (
        <>
          {sanctions.length === 0 && rate === 0 && (
            <Card color="blank" enableBorder padding="32px">
              <Container
                alignItems="center"
                direction="column"
                gap="16px"
                justifyContent="center"
                style={{ textAlign: 'center' }}
              >
                <NotFoundImg src={`${PUBLIC_URL}/assets/images/notFound.svg`} />

                <Typography color="black3" variant="body2">
                  Este proveedor no ha recibido órdenes <br /> de compra de <Strong>{label}</Strong> en los{' '}
                  <Strong>últimos 24 meses</Strong>.
                </Typography>
              </Container>
            </Card>
          )}

          {sanctions.length !== 0 && (
            <Container gap="8px">
              <TableContainer>
                <Table accessibility={{ label: title }} enableStripped>
                  <TableRow>
                    {TABLE_COLUMNS.map(({ key, label, width }) => (
                      <TableCell key={key} padding="8px 16px" width={width}>
                        <Typography fontWeight="bold" variant="caption">
                          {label}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {onGetCurrentPage().map((sanction, index) => {
                    const isLast = onGetCurrentPage().length - 1 === index

                    return (
                      <TableRow height="80px" key={`sanction-${sanction.ID}`}>
                        <TableCell disableBottomBorder={!isLast} padding="12px 16px">
                          <Typography fontWeight="bold" variant="body2">
                            {sanction.purchaseOrderCode}
                          </Typography>

                          <Typography fontSize="13px" variant="body2">
                            {sanction.purchaseOrderName}
                          </Typography>
                        </TableCell>

                        <TableCell disableBottomBorder={!isLast} padding="12px 16px">
                          <Typography fontWeight="bold" variant="body2">
                            {sanction.organization}
                          </Typography>
                        </TableCell>

                        <TableCell disableBottomBorder={!isLast} padding="12px 16px">
                          {sanction.acceptanceDate}
                        </TableCell>

                        <TableCell disableBottomBorder={!isLast} padding="12px 16px">
                          {sanction.isVoid ? (
                            <Tag color="default" text={VOID_STATES.VOIDED} />
                          ) : (
                            <Link
                              onClick={handleOnClick}
                              to={routes.sanctionDetails.replace(PATH_PARAM, dni).replace(PATH_PARAM, sanction.ID)}
                            >
                              Ver detalle
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Table>
              </TableContainer>

              {sanctions?.length > ROWS_PER_PAGE && (
                <Pagination alignment="right" count={totalPages} onChange={onChangePage} page={page} size="small" />
              )}
            </Container>
          )}

          {sanctions.length === 0 && rate !== 0 && (
            <Typography color="black3" variant="body2">
              Este proveedor no ha recibido sanciones por incumplimientos en <Strong>{label}</Strong> en los{' '}
              <Strong>últimos 24 meses</Strong>.
            </Typography>
          )}
        </>
      )}
    </Container>
  )
}

SantionsTypeTable.propTypes = {
  type: PropTypes.oneOf(Object.keys(SANCTION_TYPES)).isRequired
}

export default SantionsTypeTable
